/**
 * ScreenReaderLiveAnnouncer.tsx
 *
 * Visually hidden ARIA live regions for polite and assertive screen reader announcements.
 */

import React, { useEffect, useState } from 'react';
import { globalAccessibilityManager, type ScreenReaderAnnouncement } from './accessibility-manager.js';

export const ScreenReaderLiveAnnouncer: React.FC = () => {
  const [politeMessage, setPoliteMessage] = useState<string>('');
  const [assertiveMessage, setAssertiveMessage] = useState<string>('');

  useEffect(() => {
    return globalAccessibilityManager.subscribeAnnouncements((announcements) => {
      const latest = announcements[announcements.length - 1];
      if (!latest) return;

      if (latest.priority === 'assertive') {
        setAssertiveMessage(latest.message);
      } else {
        setPoliteMessage(latest.message);
      }
    });
  }, []);

  const srStyle: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  };

  return (
    <div id="a11y-live-announcer-container" aria-hidden="false">
      <div
        id="a11y-polite-region"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={srStyle}
      >
        {politeMessage}
      </div>
      <div
        id="a11y-assertive-region"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={srStyle}
      >
        {assertiveMessage}
      </div>
    </div>
  );
};
